import MomentUtils from '@date-io/moment';
import { Button, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Radio, RadioGroup, Select as MuiSelect, TextField } from "@material-ui/core";
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { Add as AddBoxIcon, DeleteOutlined } from "@material-ui/icons";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import moment from 'moment';
import * as React from "react";
import { ConnectedProps, connect } from 'react-redux';
import Select from 'react-select';
import { IState } from '../../redux/store';
import ReduxLanguage from "../ReduxLanguage";

import { ICustomerContractNotificationRuleFormDTO } from '../../repository/jago/model/input/ICustomerContractNotificationRuleFormDTO';
import { CustomerContractNotificationRuleType } from '../../repository/jago/model/input/ICustomerContractNotificationRuleInDTO';
import { ICustomerContractServiceInDTO } from '../../repository/jago/model/input/ICustomerContractServiceInDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import translations from '../../translations/i18next';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: 20,
    height: '100%',
    flex: 1,
  },
  formTitle: {
    fontWeight: 'bold',
    marginBottom: 20,
  },
  errorText: {
    color: 'red'
  },
  inputTitle: {
    marginTop: 10,
    marginBottom: 8,
    fontSize: 18,
    fontWeight: 'bold',
  },
  disabledButton: {
    backgroundColor: 'red'
  },
  inputTitleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  italicText: {
    fontStyle: 'italic',
  },
  customerDataColumn: {
    flex: 1,
  },
  actionButton: {
    width: 'fit-content',
    color: 'white',
    marginLeft: 20,
    padding: 4,
    paddingLeft: 10,
    paddingRight: 10,
    fontWeight: 'bold'
  },
  actionButtonText: {
    margin: 0,
    fontWeight: 'bold',
  },
  icon: {
    fontSize: 15,
    marginRight: 5,
  },
  iconCustomer: {
    marginRight: 5,
    fontSize: 25,
  },
  dateInput: {
    height: 33
  }
});

const intervalMonthsOptions = [
  { value: 1, label: 'notificationIntervalMonths_1' },
  { value: 2, label: 'notificationIntervalMonths_2' },
  { value: 3, label: 'notificationIntervalMonths_3' },
  { value: 4, label: 'notificationIntervalMonths_4' },
  { value: 6, label: 'notificationIntervalMonths_6' },
  { value: 12, label: 'notificationIntervalMonths_12' }
];

const ruleTypeOptions = [
  { value: CustomerContractNotificationRuleType.ONCE, label: 'notificationRuleType_ONCE' },
  { value: CustomerContractNotificationRuleType.PERIODIC, label: 'notificationRuleType_PERIODIC' }
];

export interface ICustomerContractNotificationRuleFormState {
  notificationRule: ICustomerContractNotificationRuleFormDTO;
  errors: Record<keyof ICustomerContractNotificationRuleFormDTO, boolean>;
  emailsLocal: string[];
  emailErrors: boolean[];
  periodicMode: 'startDate' | 'dayOfMonth'; // New field to track the selected mode for periodic notifications
}

export interface ICustomerContractNotificationRuleFormProps extends WithStyles<typeof styles>{
  onCreateEditNotificationRule: (notificationRule: ICustomerContractNotificationRuleFormDTO) => void;
  notificationRule?: ICustomerContractNotificationRuleFormDTO;
  contractServices?: ICustomerContractServiceInDTO[];
}

const mapStateToProps = (state: IState) => ({
  customerContractServices: state.credits.customerContractServices,
});

const connector = connect(mapStateToProps);
type ComponentProps = ConnectedProps<typeof connector> & ICustomerContractNotificationRuleFormProps;

class CustomerContractNotificationRuleForm extends React.Component<ComponentProps, ICustomerContractNotificationRuleFormState> {

  constructor(props: ComponentProps) {
    super(props);
    const defaultNotificationRule = {
      id: undefined,
      ruleType: CustomerContractNotificationRuleType.ONCE,
      intervalMonths: undefined,
      dayOfMonth: 1,
      specificDate: undefined,
      enabled: true,
      customerContractServices: [],
      emails: [],
      startDate: 0
    };

    // If editing existing rule and it's ONCE type, sync startDate with specificDate
    if (props.notificationRule && props.notificationRule.ruleType === CustomerContractNotificationRuleType.ONCE && props.notificationRule.specificDate) {
      props.notificationRule.startDate = props.notificationRule.specificDate;
    }

    // Determine initial periodicMode based on existing data
    let initialPeriodicMode: 'startDate' | 'dayOfMonth' = 'startDate';
    if (props.notificationRule && props.notificationRule.ruleType === CustomerContractNotificationRuleType.PERIODIC) {
      // If dayOfMonth is set and startDate is not meaningful, prefer dayOfMonth
      if (props.notificationRule.dayOfMonth && props.notificationRule.dayOfMonth > 0 && props.notificationRule.dayOfMonth <= 28) {
        initialPeriodicMode = 'dayOfMonth';
      } else if (props.notificationRule.startDate && props.notificationRule.startDate > 0) {
        initialPeriodicMode = 'startDate';
      }
    }

    this.state = {
      notificationRule: props.notificationRule ? props.notificationRule : defaultNotificationRule,
      errors: {
        id: false,
        ruleType: false,
        intervalMonths: false,
        dayOfMonth: false,
        specificDate: false,
        enabled: false,
        customerContractServices: false,
        emails: false,
        startDate: false,
      },
      emailsLocal: props.notificationRule && props.notificationRule.emails && props.notificationRule.emails.length > 0 
        ? props.notificationRule.emails 
        : [""],
      emailErrors: props.notificationRule && props.notificationRule.emails && props.notificationRule.emails.length > 0 
        ? props.notificationRule.emails.map(() => false)
        : [false],
      periodicMode: initialPeriodicMode
    };
  }

  onChangeValue(prop: keyof ICustomerContractNotificationRuleFormDTO, setError: boolean, value: any) {
    const { notificationRule, errors } = this.state;
    (notificationRule as any)[prop] = value;
    
    // If specificDate changes in ONCE mode, sync startDate
    if (prop === 'specificDate' && notificationRule.ruleType === CustomerContractNotificationRuleType.ONCE) {
      notificationRule.startDate = value;
    }
    
    // If ruleType changes, handle startDate synchronization
    if (prop === 'ruleType') {
      if (value === CustomerContractNotificationRuleType.ONCE && notificationRule.specificDate) {
        notificationRule.startDate = notificationRule.specificDate;
      }
    }
    
    if (setError) {
      if (prop === 'dayOfMonth') {
        errors[prop] = value === null || value === undefined || value < 1 || value > 28;
      } else {
        errors[prop] = !value;
      }
    }
    this.setState({ notificationRule, errors });
  }

  onSubmitForm() {
    const { onCreateEditNotificationRule } = this.props;
    const { notificationRule, emailsLocal, periodicMode } = this.state;
    
    const emailsList = emailsLocal.filter(email => email.trim().length > 0);
    
    
    const cleanNotificationRule = { ...notificationRule };
    cleanNotificationRule.emails = emailsList;
    cleanNotificationRule.enabled = true; 
    
    if (cleanNotificationRule.ruleType === CustomerContractNotificationRuleType.ONCE) {
      cleanNotificationRule.startDate = cleanNotificationRule.specificDate || 0;
      cleanNotificationRule.dayOfMonth = undefined;
      cleanNotificationRule.intervalMonths = undefined;
    } 
    else {
      if (periodicMode === 'startDate') {
        cleanNotificationRule.dayOfMonth = undefined;
      } else {
        cleanNotificationRule.startDate = 0;
      }
    }
    
    onCreateEditNotificationRule(cleanNotificationRule);
  }

  isEmailValid(email: string): boolean {
    if (!email) return false;
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
  }

  canSubmitForm() {
    const { notificationRule, emailsLocal, emailErrors, periodicMode } = this.state;
    const hasAtLeastOneEmail = emailsLocal.some(email => email.trim().length > 0);
    const hasInvalidEmails = emailsLocal.some((email, index) => email.trim().length > 0 && emailErrors[index]);
    
    if (notificationRule.ruleType === CustomerContractNotificationRuleType.ONCE) {
      return notificationRule.ruleType && notificationRule.specificDate && notificationRule.startDate && hasAtLeastOneEmail && !hasInvalidEmails;
    } else {
      const baseValid = notificationRule.ruleType && notificationRule.intervalMonths && hasAtLeastOneEmail && !hasInvalidEmails;
      
      if (periodicMode === 'dayOfMonth') {
        const isDayOfMonthValid = notificationRule.dayOfMonth !== null && 
                                  notificationRule.dayOfMonth !== undefined && 
                                  notificationRule.dayOfMonth >= 1 && 
                                  notificationRule.dayOfMonth <= 28;
        return baseValid && isDayOfMonthValid;
      } else {
        return baseValid && notificationRule.startDate;
      }
    }
  }

  onPeriodicModeChange = (newMode: 'startDate' | 'dayOfMonth') => {
    const { notificationRule } = this.state;
    
    if (newMode === 'startDate') {
      notificationRule.dayOfMonth = undefined;
    } else {
      notificationRule.startDate = 0;
    }
    
    this.setState({ 
      periodicMode: newMode,
      notificationRule
    });
  }

  public render() {
    const { classes, contractServices } = this.props;
    const { notificationRule, errors, emailsLocal, emailErrors } = this.state;
    
    // Ensure contractServices is always an array
    const safeContractServices = Array.isArray(contractServices) ? contractServices : [];
    
    const isEdit = Boolean(this.props.notificationRule);
    const canEdit = (isEdit && AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_UPDATE)) || 
      (!isEdit && AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_CREATE));

    const tomorrow = moment().add(1, 'day');

    const serviceOptions = safeContractServices.map(service => ({
      value: service.id,
      label: `${service.code || ""} - ${service.subject || ""}`,
      service: service
    }));

    const selectedServices = Array.isArray(notificationRule.customerContractServices) ? 
      notificationRule.customerContractServices.map(service => ({
        value: service.id,
        label: `${service.code || ""} - ${service.subject || ""}`,
        service: service
      })) : [];

    return (
      <MuiPickersUtilsProvider utils={MomentUtils} locale={moment.locale()}>
        <div className={classes.container}>
          
          {/* Rule Type Selection */}
          <h4 style={{ marginTop: 0, marginLeft: 0, marginBottom: 3, fontWeight: 'bold' }}>
            <ReduxLanguage languageKey={'credits.notificationRuleType'} />
            <span style={{ color: 'red' }}>*</span>
          </h4>
          <Select
            options={ruleTypeOptions}
            isSearchable={false}
            placeholder={<ReduxLanguage languageKey={'credits.notificationRuleType'} />}
            getOptionLabel={(option) => translations.t(`credits.${option.label}`)}
            value={ruleTypeOptions.find(option => option.value === notificationRule.ruleType)}
            onChange={(selectedOption) => this.onChangeValue('ruleType', true, selectedOption ? selectedOption.value : null)}
            isDisabled={!canEdit}
            styles={{
              menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
              control: (styles) => ({ ...styles, minHeight: 55, marginTop: 10, marginBottom: 10 }),
            }} 
          />

          {/* Conditional fields based on rule type */}
          {notificationRule.ruleType === CustomerContractNotificationRuleType.ONCE && (
            <div style={{ marginLeft: 0 }}>
              <h3 style={{ marginTop: 20, marginLeft: 0, marginBottom: 3 }}>
                <ReduxLanguage languageKey={'credits.notificationSpecificDate'} />
                <span style={{ color: 'red' }}>*</span>
              </h3>
              <KeyboardDatePicker
                disableToolbar
                style={{ width: '100%' }}
                variant="dialog"
                format="DD/MM/YYYY"
                margin="dense"
                inputVariant="outlined"
                inputProps={{
                  className: classes.dateInput
                }}
                clearable
                disabled={!canEdit}
                minDate={tomorrow}
                id="date-picker-inline"
                placeholder={translations.t('forms.pickDate') + ' (dd/mm/yyyy)'}
                value={notificationRule.specificDate ? moment(notificationRule.specificDate) : null}
                onChange={e => this.onChangeValue('specificDate', true, e?.valueOf())}
              />
            </div>
          )}

          {notificationRule.ruleType === CustomerContractNotificationRuleType.PERIODIC && (
            <div>
              <h4 style={{ marginTop: 20, marginLeft: 0, marginBottom: 3, fontWeight: 'bold' }}>
                <ReduxLanguage languageKey={'credits.notificationIntervalMonths'} />
                <span style={{ color: 'red' }}>*</span>
              </h4>
              <Select
                options={intervalMonthsOptions}
                isSearchable={false}
                placeholder={<ReduxLanguage languageKey={'credits.notificationIntervalMonths'} />}
                getOptionLabel={(option) => translations.t(`credits.${option.label}`)}
                value={intervalMonthsOptions.find(option => option.value === notificationRule.intervalMonths)}
                onChange={(selectedOption) => this.onChangeValue('intervalMonths', true, selectedOption ? selectedOption.value : null)}
                isDisabled={!canEdit}
                styles={{
                  menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
                  control: (styles) => ({ ...styles, minHeight: 55, marginTop: 10, marginBottom: 10 }),
                }} 
              />

              <h4 style={{ marginTop: 20, marginLeft: 0, marginBottom: 3, fontWeight: 'bold' }}>
                <ReduxLanguage languageKey={'credits.notificationPeriodicMode'} />
                <span style={{ color: 'red' }}>*</span>
              </h4>
              <RadioGroup
                row
                value={this.state.periodicMode}
                onChange={(e) => this.onPeriodicModeChange(e.target.value as 'startDate' | 'dayOfMonth')}
                style={{ marginBottom: 20 }}
              >
                <FormControlLabel
                  value="startDate"
                  control={<Radio />}
                  label={<ReduxLanguage languageKey={'credits.notificationPeriodicModeStartDate'} />}
                />
                <FormControlLabel
                  value="dayOfMonth"
                  control={<Radio />}
                  label={<ReduxLanguage languageKey={'credits.notificationPeriodicModeDayOfMonth'} />}
                />
              </RadioGroup>

              {this.state.periodicMode === 'startDate' && (
                <>
                  <KeyboardDatePicker
                    disableToolbar
                    style={{ width: '100%' }}
                    variant="dialog"
                    format="DD/MM/YYYY"
                    margin="dense"
                    inputVariant="outlined"
                    inputProps={{
                      className: classes.dateInput
                    }}
                    clearable
                    disabled={!canEdit}
                    minDate={tomorrow}
                    id="start-date-picker"
                    placeholder={translations.t('forms.pickDate') + ' (dd/mm/yyyy)'}
                    value={notificationRule.startDate ? moment(notificationRule.startDate) : null}
                    onChange={e => this.onChangeValue('startDate', true, e?.valueOf())}
                  />
                </>
              )}

              {this.state.periodicMode === 'dayOfMonth' && (
                <>
                  <TextField
                    autoComplete='off'
                    id="day-of-month"
                    label={<ReduxLanguage languageKey={'credits.notificationDayOfMonth'} />}
                    variant="outlined"
                    fullWidth
                    required
                    disabled={!canEdit}
                    type="number"
                    error={errors.dayOfMonth}
                    helperText={errors.dayOfMonth ? translations.t('credits.dayOfMonthValidationError') : translations.t('credits.dayOfMonthHelp')}
                    value={notificationRule.dayOfMonth || ''}
                    style={{ marginTop: 10 }}
                    onChange={e => {
                      const value = e.target.value;
                      if (value === '') {
                        this.onChangeValue('dayOfMonth', true, null);
                      } else {
                        const numValue = parseInt(value);
                        this.onChangeValue('dayOfMonth', true, numValue);
                      }
                    }}
                  />
                </>
              )}
            </div>
          )}

          <h3 style={{ fontWeight: 'bold', marginTop: 20 }}>
            <ReduxLanguage languageKey={'credits.notificationEmails'} />
            <span style={{ color: 'red', marginLeft: 2 }}>*</span>
          </h3>
          <h5 style={{ fontWeight: 'normal' }}>
            <ReduxLanguage languageKey={'credits.notificationEmailsHelp'} />
          </h5>
          {emailsLocal.map((emailLocal, index) => (
            <div key={index} style={{display:'flex',flexDirection:'row',alignItems:'center',marginBottom:10}}>
              <TextField
                autoComplete='off'
                id="outlined-basic"
                label={emailLocal ? "" : translations.t('credits.enterEmail')}
                variant="outlined"
                fullWidth
                disabled={!canEdit}
                error={emailErrors[index]}
                helperText={emailErrors[index] ? translations.t('forms.invalidEmail') : ''}
                value={emailLocal}
                style={{ marginBottom: 10, marginTop:10, maxWidth:400 }}
                onChange={e => {
                  let newValue = e.target.value;
                  let updatedEmails = emailsLocal.map((item, i) => {
                    if (i === index) return newValue;
                    else return item;
                  });
                  let updatedErrors = emailsLocal.map((item, i) => {
                    if (i === index) return newValue.trim().length > 0 && !this.isEmailValid(newValue);
                    else return emailErrors[i];
                  });
                  this.setState({ emailsLocal: updatedEmails, emailErrors: updatedErrors });
                }}
              />
              {!canEdit ? null : (
                <IconButton 
                  onClick={() => this.setState({emailsLocal: emailsLocal.filter(e => e !== emailLocal)})} 
                  disabled={!emailsLocal || emailsLocal.length <= 1}
                  style={{ marginBottom: emailErrors[index] ? 20 : 0 }}
                >
                  <DeleteOutlined style={{ color: !emailsLocal || emailsLocal.length <= 1 ? 'grey' : 'red' }} />
                </IconButton>
              )}
            </div>
          ))}
          {!canEdit ? null : (
            <IconButton 
              onClick={() => this.setState({emailsLocal: [...emailsLocal, ""]})} 
              disabled={false}
              style={{marginTop:'-20px',width:'fit-content'}}
            >
              <AddBoxIcon style={{ color: 'green' }} />
            </IconButton>
          )}

          <div style={{ marginTop: 20 }}>
            <div className={classes.inputTitle}>
              <ReduxLanguage languageKey={'credits.notificationAssociatedServices'} />
            </div>
            <h5 style={{ fontWeight: 'normal', margin: '5px 0 10px 0', color: 'gray' }}>
              <ReduxLanguage languageKey={'credits.notificationAssociatedServicesHelp'} />
            </h5>
            <Select
              isMulti
              options={serviceOptions}
              isSearchable={true}
              placeholder={<ReduxLanguage languageKey={'credits.selectContractServices'} />}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value.toString()}
              value={selectedServices}
              onChange={(selectedOptions) => {
                const services = Array.isArray(selectedOptions) 
                  ? selectedOptions.map(option => option.service)
                  : [];
                this.onChangeValue('customerContractServices', false, services);
              }}
              isDisabled={!canEdit}
              styles={{
                menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
                control: (styles) => ({ ...styles, minHeight: 55, marginTop: 10, marginBottom: 10 }),
              }} 
            />
          </div>

          <div style={{ flex: 1, marginRight: 10 }}>
          </div>
          
          {canEdit && (
            <div style={{position:'sticky',bottom:'0',paddingTop:'20px',backgroundColor:'white',marginLeft:'-20px',marginRight:'-20px',zIndex:1}}>
              <Button
                variant="contained"
                disabled={!this.canSubmitForm()}
                style={{ width: '100%', opacity: this.canSubmitForm() ? 1 : 0.5, backgroundColor: '#5AC0B1', color: 'white', borderRadius: 0, height: 45, fontWeight: 'bold'  }}
                onClick={() => this.onSubmitForm()}
              >
                <ReduxLanguage languageKey="forms.save" />
              </Button>
            </div>
          )}
        </div>
      </MuiPickersUtilsProvider>
    );
  }
}

export default withStyles(styles)(connector(CustomerContractNotificationRuleForm)); 